import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Container } from 'reactstrap';

import formatIntl from '../Common/formatIntl';
import SectionHeader from '../Common/SectionHeader';
import SectionHeadingField from '../Common/SectionHeadingField';
import { DEFAULT_GOOGLE_WEIGHTS } from '../Common/pageFontUtils';
import { withLayout } from '../PuckLayout';
import { usePageId } from '../../../../helpers/PageIdContext';
import Loading from '../../../Loading';
import FontRow from './FontRow';
import FontsField from './FontsField';
import useResolvedFonts from './useResolvedFonts';

const isFontSelectionMissing = (font) => {
  if (!font) return true;

  const source = font.source || 'uploaded';
  if (source === 'google') {
    return !font.googleFamily;
  }

  return !font.uuid && !font.url;
};

const PuckFontsRender = ({
  fonts,
  sectionHeading,
  previewText: initialPreviewText,
  allowDownloads,
  layout,
  puck,
}) => {
  const intl = useIntl();
  const pageId = usePageId();
  const { resolvedFonts, loading } = useResolvedFonts({ fonts, pageId });
  const previewText = initialPreviewText || intl.formatMessage({ defaultMessage: 'The quick brown fox jumps over the lazy dog' });

  const header = (
    <SectionHeader
      title={sectionHeading?.title}
      subtitle={sectionHeading?.subtitle}
      align={sectionHeading?.align}
    />
  );
  const visibleFonts = resolvedFonts.filter(Boolean);

  const isEditing = puck?.isEditing;
  const displayFonts = isEditing
    ? visibleFonts
    : visibleFonts.filter(font => !isFontSelectionMissing(font));

  if (displayFonts.length === 0 && !loading) {
    if (isEditing) {
      return (
        <div className="puck-fonts puck-fonts--empty puck-component-info-text center">
          {intl.formatMessage({ defaultMessage: 'No fonts added yet. Use the settings panel to add fonts.' })}
        </div>
      );
    }
    return null;
  }

  return (
    <Container>
      <div className="puck-fonts">
        {header}
        {loading && visibleFonts.length === 0 ? (
          <div className="puck-fonts-loading center"><Loading /></div>
        ) : (
          <div className={`puck-fonts-list puck-fonts-list--${layout || 'rows'}`}>
            {displayFonts.map((font, index) => (
              <div key={font.localKey || `${font.source}-${font.uuid || font.googleFamily || index}`} className="puck-fonts-row-wrap">
                <FontRow
                  font={font}
                  previewText={previewText}
                  allowDownloads={!!allowDownloads}
                  isEditing={!!isEditing}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
};

PuckFontsRender.propTypes = {
  fonts: PropTypes.array,
  sectionHeading: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    align: PropTypes.string,
  }),
  previewText: PropTypes.string,
  allowDownloads: PropTypes.bool,
  layout: PropTypes.string,
  puck: PropTypes.object,
};

PuckFontsRender.defaultProps = {
  fonts: [],
  sectionHeading: null,
  previewText: '',
  allowDownloads: false,
  layout: 'columns',
  puck: null,
};

const PuckFontsConfig = {
  fields: {
    sectionHeading: {
      type: 'custom',
      get label() { return formatIntl('Section Heading'); },
      render: (props) => <SectionHeadingField {...props} />,
    },
    fonts: {
      type: 'custom',
      get label() { return formatIntl('Fonts'); },
      render: (props) => <FontsField {...props} />,
    },
    previewText: {
      type: 'text',
      get label() { return formatIntl('Preview text'); },
    },
    layout: {
      type: 'radio',
      get label() { return formatIntl('Layout'); },
      options: [
        { get label() { return formatIntl('Rows'); }, value: 'rows' },
        { get label() { return formatIntl('Columns'); }, value: 'columns' },
      ],
    },
    allowDownloads: {
      type: 'radio',
      get label() { return formatIntl('Allow downloads'); },
      options: [
        { get label() { return formatIntl('Yes'); }, value: true },
        { get label() { return formatIntl('No'); }, value: false },
      ],
    },
  },
  defaultProps: {
    fonts: [
      {
        source: 'google',
        uuid: '',
        path: '',
        fileName: '',
        name: 'Inter',
        description: formatIntl('Used for headings and titles'),
        googleFamily: 'Inter',
        googleWeight: '400',
        googleWeights: DEFAULT_GOOGLE_WEIGHTS,
        googleFiles: {},
        googleSearch: '',
      },
      {
        source: 'google',
        uuid: '',
        path: '',
        fileName: '',
        name: 'Montserrat',
        description: formatIntl('Used for paragraphs and body text'),
        googleFamily: 'Montserrat',
        googleWeight: '400',
        googleWeights: DEFAULT_GOOGLE_WEIGHTS,
        googleFiles: {},
        googleSearch: '',
      },
    ],
    sectionHeading: {
      title: formatIntl('Header'),
      subtitle: formatIntl('Subheader'),
      align: 'left',
    },
    previewText: formatIntl('The quick brown fox jumps over the lazy dog'),
    layout: 'columns',
    allowDownloads: false,
  },
  render: (props) => <PuckFontsRender {...props} />,
};

export default withLayout(PuckFontsConfig);
